import { BasePropertyDTO, IntPropertyDTO, ValidationType } from 'business/models/Models';

export function timePropertyToPickerValue(hhmmss: string): string {
  if (!hhmmss) {
    return '';
  }
  return hhmmss.slice(0, 5);
}

export function pickerValueToTimeProperty(pickerValue: string): string {
  if (!pickerValue) {
    return '';
  }
  return pickerValue.length === 5 ? `${pickerValue}:00` : pickerValue;
}

export function validateProperty(
  property: any,
  props: BasePropertyDTO[] = null,
): string | null {
  if (!property.validations || property.validations.length === 0) {
    return null;
  }

  for (const rule of property.validations) {
    const value = property.value;

    switch (rule.type) {
      case ValidationType.Required:
        if (value === null || value === undefined || value === '') {
          return rule.errorMessage || 'This field is required.';
        }
        break;

      case ValidationType.MinValue:
        if (value < rule.minValue) {
          return rule.errorMessage || `Value must be ≥ ${rule.minValue}`;
        }
        break;

      case ValidationType.MaxValue:
        if (value > rule.maxValue) {
          return rule.errorMessage || `Value must be ≤ ${rule.maxValue}`;
        }
        break;

      case ValidationType.Range:
        if (value < rule.minValue || value > rule.maxValue) {
          return (
            rule.errorMessage ||
            `Value must be between ${rule.minValue} and ${rule.maxValue}`
          );
        }
        break;

      case ValidationType.StringLength:
        if (
          typeof value === 'string' &&
          (value.length < rule.minLength || value.length > rule.maxLength)
        ) {
          return (
            rule.errorMessage ||
            `Length must be between ${rule.minLength} and ${rule.maxLength}`
          );
        }
        break;

      case ValidationType.RegexPattern:
        if (!new RegExp(rule.pattern).test(value)) {
          return rule.errorMessage || 'Value does not match required pattern.';
        }
        break;

      case ValidationType.MaxProduct:
        const operandProp = props.find((p) => p.name == rule.operandProperty);
        if (operandProp) {
          const operandValue = operandProp as IntPropertyDTO;
          if (operandProp && value * operandValue.value > rule.maxProduct) {
            const errorMessage =
              rule.errorMessage?.replace(rule.operandProperty, operandProp.displayName) ||
              `Product with '${operandProp.displayName}' property must be ≤ ${rule.maxProduct}`;
            return errorMessage;
          }
        }
        break;
      default:
        break;
    }
  }

  return null;
}

export function normalizeTag(text: string): string {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    return trimmed;
  }

  const content = trimmed.slice(1, -1);
  const baseTagName = content.split(':')[0];
  return `{${baseTagName}}`;
}

export function buildPrintLineValue(
  selectedIds: number[],
  currentValue: string,
  printLineParametersItems: Array<{
    id: number;
    text: string;
    value: string;
    group: string;
  }>,
): string {
  const existingByNormalizedTag = new Map<string, string[]>();

  currentValue
    .split('|')
    .map((token) => token.trim())
    .filter(Boolean)
    .forEach((token) => {
      const normalized = normalizeTag(token);
      const values = existingByNormalizedTag.get(normalized) || [];
      values.push(token);
      existingByNormalizedTag.set(normalized, values);
    });

  return selectedIds
    .map((id) => {
      const selectedItem = printLineParametersItems.find((item) => item.id === id);
      if (!selectedItem) {
        return null;
      }

      const normalized = normalizeTag(selectedItem.value);
      const existingValues = existingByNormalizedTag.get(normalized);
      if (existingValues && existingValues.length > 0) {
        return existingValues.shift() || selectedItem.value;
      }

      return selectedItem.value;
    })
    .filter((value): value is string => Boolean(value))
    .join('|');
}

export function selectedPrintLineParametersItems(
  prop: any,
  printLineParametersItems: Array<{
    id: number;
    text: string;
    value: string;
    group: string;
  }>,
): Array<{ id: number; text: string; value: string; group: string }> {
  if (!prop?.value || typeof prop.value !== 'string') {
    return [];
  }

  const tagPattern = /\{([^}]+)\}/g;
  const matches = prop.value.match(tagPattern) || [];

  const lookup = new Map(printLineParametersItems.map((item) => [item.value, item.id]));

  const ids = matches
    .map((text) => {
      const direct = lookup.get(text);
      if (direct !== undefined) {
        return direct;
      }
      return printLineParametersItems.find(
        (item) => normalizeTag(item.value) === normalizeTag(text),
      )?.id;
    })
    .filter((id): id is number => id !== undefined);

  const seen = new Set<number>();
  const uniqueIds = ids.filter((id) => {
    if (seen.has(id)) {
      return false;
    }
    seen.add(id);
    return true;
  });

  return uniqueIds
    .map((id) => printLineParametersItems.find((item) => item.id === id))
    .filter(
      (item): item is { id: number; text: string; value: string; group: string } =>
        item !== undefined,
    );
}
